import React, { useState } from 'react'
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Formik, Form, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { useDispatch } from 'react-redux'

import { AppDispatch } from '../../../app/store'
import { changeUserPassword } from '../userSlice'

const validationSchema = Yup.object({
  current_password: Yup.string()
    .required('Current password is required')
    .min(8, 'Password must be at least 8 characters'),
  new_password: Yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirm_password: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('new_password')], 'Passwords must match'),
})

interface ChangePasswordFormProps {
  onSuccess?: () => void
}

interface FormValues {
  current_password: string
  new_password: string
  confirm_password: string
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ onSuccess }) => {
  const dispatch = useDispatch<AppDispatch>()
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  })

  const handleClickShowPassword = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }))
  }

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const handleSubmit = async (
    values: FormValues,
    { resetForm, setSubmitting }: FormikHelpers<FormValues>
  ) => {
    try {
      setIsLoading(true)
      setErrorMessage('')
      setSuccessMessage('')

      await dispatch(
        changeUserPassword({
          current_password: values.current_password,
          new_password: values.new_password,
          confirm_password: values.confirm_password,
        })
      ).unwrap()

      setSuccessMessage('Password changed successfully!')
      resetForm()
      setTimeout(() => {
        setSuccessMessage('')
        onSuccess?.()
      }, 3000)
    } catch (error: any) {
      const message = error?.message || 'Failed to change password. Please try again.'
      setErrorMessage(message)
    } finally {
      setIsLoading(false)
      setSubmitting(false)
    }
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Change Your Password
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Enter your current password and choose a new password. Password must be at least 8
        characters and contain an uppercase letter, lowercase letter, and a number.
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      <Formik
        initialValues={{
          current_password: '',
          new_password: '',
          confirm_password: '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, values, isValid, dirty, setFieldValue }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  name="current_password"
                  type={showPasswords.current ? 'text' : 'password'}
                  value={values.current_password}
                  onChange={(e) => setFieldValue('current_password', e.target.value)}
                  error={touched.current_password && Boolean(errors.current_password)}
                  helperText={touched.current_password && errors.current_password}
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleClickShowPassword('current')}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  name="new_password"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={values.new_password}
                  onChange={(e) => setFieldValue('new_password', e.target.value)}
                  error={touched.new_password && Boolean(errors.new_password)}
                  helperText={touched.new_password && errors.new_password}
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleClickShowPassword('new')}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirm_password"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={values.confirm_password}
                  onChange={(e) => setFieldValue('confirm_password', e.target.value)}
                  error={touched.confirm_password && Boolean(errors.confirm_password)}
                  helperText={touched.confirm_password && errors.confirm_password}
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleClickShowPassword('confirm')}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoading || !isValid || !dirty}
                    sx={{ minWidth: 120 }}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Change Password'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Paper>
  )
}

export default ChangePasswordForm
